import { Cesto, DEFAULT_BASE_URL } from './cesto';
import { preconnect } from './preconnect';
import type { InvestCallbacks, InvestMode, InvestTheme } from './types';

export interface BindInvestButtonsOptions extends InvestCallbacks {
  /** Fallback key for elements without a data-cesto-pk attribute. */
  apiKey?: string;
  /** Invest app origin override (defaults to https://app.cesto.co). */
  baseUrl?: string;
  /** Fallback mode for elements without a data-cesto-mode attribute. */
  mode?: InvestMode;
  /**
   * Fallback theme; data-cesto-accent / data-cesto-radius /
   * data-cesto-scheme attributes override it field by field.
   */
  theme?: InvestTheme;
  /** Root to search under. Defaults to `document`. */
  root?: ParentNode;
}

function parseMode(raw: string | undefined): InvestMode | undefined {
  return raw === 'dialog' || raw === 'popup' ? raw : undefined;
}

/**
 * Merges the data-cesto-* theme attributes (accent, background, foreground,
 * muted, muted-foreground, border, radius, scheme) over the options-level
 * theme. Values are not validated (the invest app does that server-side);
 * empty attributes are ignored.
 */
function parseTheme(
  dataset: DOMStringMap,
  fallback: InvestTheme | undefined,
): InvestTheme | undefined {
  const theme: InvestTheme = { ...fallback };
  if (dataset.cestoAccent) theme.accent = dataset.cestoAccent;
  if (dataset.cestoBackground) theme.background = dataset.cestoBackground;
  if (dataset.cestoForeground) theme.foreground = dataset.cestoForeground;
  if (dataset.cestoMuted) theme.muted = dataset.cestoMuted;
  if (dataset.cestoMutedForeground) {
    theme.mutedForeground = dataset.cestoMutedForeground;
  }
  if (dataset.cestoBorder) theme.border = dataset.cestoBorder;
  if (dataset.cestoRadius) theme.radius = dataset.cestoRadius;
  if (dataset.cestoScheme) {
    theme.scheme = dataset.cestoScheme as InvestTheme['scheme'];
  }
  return Object.values(theme).some(Boolean) ? theme : undefined;
}

/**
 * One loader per pk + baseUrl pair so repeat clicks focus the open popup.
 *
 * Deliberately never pruned, and the click listeners below are never removed
 * (LOW-6). Both are bounded in practice by the number of DISTINCT pk+baseUrl
 * pairs on a page — one, for every real integration — not by the number of
 * buttons or clicks. `bindInvestButtons` is for static/CMS pages where elements
 * are added but not churned; a long-lived SPA that repeatedly mounts and
 * unmounts invest buttons should use `@cesto/react`'s
 * `useCestoInvest` (or hold its own `Cesto`) rather than re-binding, so
 * listeners follow the component lifecycle.
 */
const clients = new Map<string, Cesto>();

function getClient(apiKey: string, baseUrl?: string): Cesto {
  const key = `${apiKey}|${baseUrl ?? ''}`;
  let client = clients.get(key);
  if (!client) {
    client = new Cesto({ apiKey, baseUrl });
    clients.set(key, client);
  }
  return client;
}

/**
 * Declarative binding for static sites:
 *
 *   <button data-cesto-invest="golden-age" data-cesto-pk="cesto_pk_...">
 *     Invest now
 *   </button>
 *
 * Optional attributes: data-cesto-amount (USD prefill), data-cesto-base-url,
 * data-cesto-mode ("dialog" or "popup"; dialog is the default), and the
 * theme trio data-cesto-accent, data-cesto-radius, data-cesto-scheme.
 * The key resolves from data-cesto-pk first, then options.apiKey.
 * Elements are bound once; calling again only picks up new elements.
 *
 * Returns the number of elements newly bound.
 */
export function bindInvestButtons(
  options: BindInvestButtonsOptions = {},
): number {
  const root = options.root ?? document;
  const elements = root.querySelectorAll<HTMLElement>('[data-cesto-invest]');
  let bound = 0;

  for (const element of Array.from(elements)) {
    if (element.dataset.cestoBound === 'true') continue;
    element.dataset.cestoBound = 'true';
    bound += 1;

    // Warm the connection to the app origin at bind time (page load), before
    // the user clicks, so a cold open skips DNS + TLS on the critical path
    // (DLG-6). Best-effort; a bad base-url is surfaced by the click handler.
    try {
      const bindBaseUrl =
        element.dataset.cestoBaseUrl ?? options.baseUrl ?? DEFAULT_BASE_URL;
      preconnect(new URL(bindBaseUrl).origin);
    } catch {
      // Invalid base-url — ignore here; the click handler reports it.
    }

    element.addEventListener('click', (event) => {
      // Bound elements are frequently anchors pointing at a basket page, kept
      // as a no-JS fallback. Once this handler exists, the anchor must NOT
      // also navigate — and it silently did whenever anything below threw
      // (most reachably `isAllowedBaseOrigin` rejecting a `data-cesto-base-url`),
      // so a misconfigured integration "worked" by quietly opening the basket
      // page instead of the invest flow, with nothing in the console.
      event.preventDefault();

      const basket = element.dataset.cestoInvest;
      const apiKey = element.dataset.cestoPk ?? options.apiKey;
      if (!basket || !apiKey) {
        console.warn(
          '[Cesto] data-cesto-invest element is missing a basket slug or a publishable key',
          element,
        );
        return;
      }

      const baseUrl = element.dataset.cestoBaseUrl ?? options.baseUrl;
      const mode = parseMode(element.dataset.cestoMode) ?? options.mode;
      const theme = parseTheme(element.dataset, options.theme);
      const amountRaw = element.dataset.cestoAmount;
      const parsedAmount =
        amountRaw !== undefined ? Number.parseFloat(amountRaw) : Number.NaN;
      const amountUsd =
        Number.isFinite(parsedAmount) && parsedAmount > 0
          ? parsedAmount
          : undefined;

      // invest() runs synchronously inside the click handler on purpose:
      // popup blockers only allow window.open during a user gesture.
      let client: Cesto;
      try {
        client = getClient(apiKey, baseUrl);
      } catch (error) {
        // Almost always a rejected `data-cesto-base-url`. Say so loudly — this
        // is a developer error at integration time, and the alternative is a
        // dead button.
        console.error('[Cesto] could not open the invest flow', error);
        return;
      }
      client.invest({
        basket,
        ...(mode !== undefined ? { mode } : {}),
        ...(theme !== undefined ? { theme } : {}),
        ...(amountUsd !== undefined ? { amountUsd } : {}),
        // Every callback in `InvestCallbacks`, enumerated because `options`
        // also carries config keys (apiKey, baseUrl, mode, theme, root) that
        // must not reach `invest()`. Enumerating means this list can drift
        // from the interface — it already had: `onReady`, `onVerified` and
        // `onPositionClosed` were accepted by the type and then silently
        // dropped here, so a data-attribute integrator passing them got
        // nothing. Add new callbacks in BOTH places.
        onReady: options.onReady,
        onVerified: options.onVerified,
        onSuccess: options.onSuccess,
        onScheduled: options.onScheduled,
        onError: options.onError,
        onPositionClosed: options.onPositionClosed,
        onHandoff: options.onHandoff,
        onClose: options.onClose,
      });
    });
  }

  return bound;
}
